import { getDiscountPoolById, useGetAllDiscountPoolsForSelect, useIsLoadingDiscountPools } from 'discounts/redux'
import { AppServices } from 'middleware'
import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import { useParams } from 'react-router-dom'
import styled from 'styled-typed'
import { ComboboxField } from 'uiComponents/combobox/combobox'
import { UploadInput } from 'uiComponents/input'
import { removeAllMessages } from 'uiComponents/messages/actions'
import InlineMessage from 'uiComponents/messages/inlineMessage'
import { Body, StyledATag } from 'uiComponents/typography'
import { downloadFile } from 'utils/downloadFile'
import { useUploadDiscountCodeMutation } from 'discounts/reduxQuery'
import { getHashParam, removeHash } from 'utils/hash'
import { BaseRouteParams } from 'hocs'
import { showSuccessMessage } from 'uiComponents/message/message'
import { useAppSelector } from 'store/hooks'
import './uploadCsv.scss'
import { Trans, useTranslation } from 'translations'

const StyledDownloadLink = styled.span`
    color: ${(props) => props.theme.colors.boyBlue};
    text-decoration: underline;
    cursor: pointer;
`

type FileUploadError = { header?: string; body?: string }

export const UploadCsv = () => {
    const { t } = useTranslation()
    const { accountSlug } = useParams<BaseRouteParams>()
    const [upload] = useUploadDiscountCodeMutation()
    const dispatch = useDispatch()
    const [fileUploadErrors, setFileUploadErrors] = useState<FileUploadError>()
    const discountPools = useGetAllDiscountPoolsForSelect()
    const loadingPools = useIsLoadingDiscountPools()
    const [selectedPoolUuid, setSelectedPoolUuid] = useState<string | null>(getHashParam('poolUuid') ?? null)
    const selectedPool = useAppSelector(getDiscountPoolById(selectedPoolUuid ?? ''))

    const onUpload = (file: File): Promise<any> => {
        if (!selectedPoolUuid) {
            setFileUploadErrors({
                body: t('discounts.addDiscount.uploadCsv.selectPoolError'),
                header: t('discounts.addDiscount.uploadCsv.uploadErrorHeader'),
            })
            return Promise.reject()
        }

        setFileUploadErrors(undefined)

        return upload({ accountSlug, file, poolUuid: selectedPoolUuid })
            .unwrap()
            .then((response) => {
                showSuccessMessage(
                    selectedPool?.name
                        ? t('discounts.addDiscount.uploadCsv.uploadToPoolSuccess', {
                              filename: response.filename,
                              pool: selectedPool?.name,
                          })
                        : t('discounts.addDiscount.uploadCsv.uploadSuccess', { filename: response.filename }),
                    'upload_success',
                )
                return {
                    file_name: response.filename,
                    file_id: response.uploadId,
                }
            })
            .catch((error) => {
                const message =
                    error?.response?.error?.message || t('discounts.addDiscount.uploadCsv.genericError')

                setFileUploadErrors({ body: message, header: t('discounts.addDiscount.uploadCsv.uploadErrorHeader') })

                throw message
            })
    }

    return (
        <div className="upload-csv-container">
            <div className="upload-csv-title">{t('discounts.addDiscount.uploadCsv.title')}</div>
            <div className="upload-csv">
                <div className="select-pool">
                    <ComboboxField
                        id="poolUuid"
                        label={t('discounts.addDiscount.uploadCsv.poolLabel')}
                        onChange={(value) => setSelectedPoolUuid(value as string | null)}
                        options={discountPools}
                        loading={loadingPools}
                        value={selectedPoolUuid}
                        disablePortal={true}
                    />
                    <div className="explanation">{t('discounts.addDiscount.uploadCsv.explanation')}</div>
                </div>
                <UploadInput
                    className={selectedPoolUuid ? 'visible' : ''}
                    type="file"
                    acceptedTypesMsg="CSV"
                    acceptedTypes="text/csv"
                    extensionCheckOnly
                    onUpload={onUpload}
                    handleUploadSuccessResponse={removeHash}
                    uploadArea
                    inputId="upload"
                    fileSizeLimit={16}
                    parseErrorMessage
                    replaceTopMessages={(id, status, text) => {
                        if (id === 'validation_error') {
                            setFileUploadErrors({
                                body: text,
                                header: t('discounts.addDiscount.uploadCsv.validationErrorHeader'),
                            })
                        }
                    }}
                    removeAllMessages={() => dispatch(removeAllMessages())}
                />
                {fileUploadErrors && (
                    <div style={{ marginBottom: '1rem' }}>
                        <InlineMessage
                            type="error"
                            header={fileUploadErrors?.header}
                            onClose={() => setFileUploadErrors(undefined)}
                        >
                            {fileUploadErrors?.body}
                        </InlineMessage>
                    </div>
                )}
                <Body size={2} color="light" style={{ paddingTop: '.5rem', userSelect: 'none' }}>
                    <Trans
                        i18nKey="discounts.addDiscount.uploadCsv.formatNote"
                        components={{
                            download: (
                                <StyledDownloadLink
                                    onClick={() => {
                                        downloadFile(
                                            `${AppServices.accountService.backofficeEndpoint}api/v1/discount_codes_upload_example/`,
                                            t('discounts.addDiscount.uploadCsv.templateFileName'),
                                        )
                                    }}
                                />
                            ),
                            anchor: <StyledATag href="https://support.convious.com/help/discount-codes" target="_blank" />,
                        }}
                    />
                </Body>
            </div>
        </div>
    )
}
